import {
  type Analytics,
  analytics as GCInstantAnalytics,
  globalTime,
} from '@play-co/gcinstant';
import StateObserver from 'src/StateObserver';
import {
  ChannelProperty,
  FirstEntryUserProperties,
  LastEntryUserProperties,
  PayloadProperties,
} from 'src/lib/analytics/properties';
import { duration } from 'src/replicant/utils/duration';
import { hasWebPlayerCredentials, PurchaseInfo } from '@play-co/replicant';
import type { UserProperties } from './analytics/properties';
import { GCInstant } from './gcinstant';
import { getLaunchScene } from './stateUtils';
import { hasValidSaveState } from 'src/replicant/match2/savestate/match2-savestate.getters';
import { isPlatformNative } from './native/native.util';
import { getOpenWebBehavior } from 'src/replicant/ruleset/openweb';
import config from 'src/replicant/config';
import PNsManager from 'src/game/native/push.notifications';
import { getDefaultRichMenu } from 'src/replicant/chatbot/chatbot.getters';
import { getCapturedLyAdsClickID } from './yahooAds';

export const analytics = GCInstantAnalytics as Analytics<UserProperties>;

function getPlatform(): { app: string; os: string } {
  const entryPoint = GCInstant.entryPointName;

  const os = GCInstant.osType || 'UNKNOWN';

  let app: string;

  if (os === 'IOS' || os === 'ANDROID') {
    if (/messenger/i.test(navigator.userAgent)) {
      app = 'MESSENGER';
    } else if (/fbav/i.test(navigator.userAgent)) {
      app = 'FACEBOOK';
    } else {
      // 1. Set FACEBOOK for known Facebook entry points
      if (
        entryPoint === 'bookmark' ||
        entryPoint === 'facebook_gaming_tab' ||
        entryPoint === 'facebook_web' ||
        entryPoint === 'feed' ||
        entryPoint === 'group' ||
        entryPoint === 'notification' ||
        entryPoint === 'web_games_hub'
      ) {
        app = 'FACEBOOK';
      }

      // 2. Set MESSENGER for known Messenger entry points
      else if (
        entryPoint === 'game_composer' ||
        entryPoint === 'games_hub' ||
        entryPoint === 'menu' ||
        entryPoint === 'thread_suggestion' ||
        entryPoint === 'video_call'
      ) {
        app = 'MESSENGER';
      }

      // 3. Set UNKNOWN for everything else
      else {
        app = 'UNKNOWN';
      }
    }
  } else if (os === 'IOS_APP') {
    app = 'NATIVE';
  } else {
    if (document.referrer.indexOf('www.messenger.com') !== -1) {
      app = 'MESSENGER';
    } else if (document.referrer.indexOf('.facebook.com') !== -1) {
      app = 'FACEBOOK';
    } else {
      app = 'UNKNOWN';
    }
  }

  return { app, os };
}

function getPurchaseDaysElapsed(
  now: number,
  history: PurchaseInfo[],
): number | undefined {
  if (!history.length) {
    return undefined;
  }

  const delta = now - history[history.length - 1].purchase_time;
  return Math.max(0, delta) / duration({ days: 1 });
}

export async function sendEntryFinalAnalytics(args: {
  supportsWebP: boolean;
  supportsAvif: boolean;
}): Promise<void> {
  const { user } = StateObserver.getState();
  const now = globalTime.getNow();
  const entryData = GCInstant.entryData;
  const realtimeProps = analytics.getUserProperties();

  const $platform = getPlatform();

  const purchaseHistory = StateObserver.replicant.getPurchaseHistory();

  // EntryFinal event properties
  const entryFinalEventProps: { [key: string]: any } = {
    feature: entryData.feature,
    $subFeature: entryData.$subFeature,

    // Friends origin attribution (passed through chatbot OA payload)
    originFeature: entryData.originFeature,
    originSubFeature: entryData.originSubFeature,

    // TODO: Deprecated, remove after 2020-11-21
    // UpdateAsync
    $sourceUpdatePushExplicit: entryData.$pushExplicit,
    $sourceUpdateStrategy: entryData.$strategy,

    // selection count from inviteAsync
    sourceSelectionCount: entryData.selectionCount,

    // THUG-945
    $capHoursIndex: entryData.$capHoursIndex,
    $capHoursStart: entryData.$capHoursStart,
    $capHoursEnd: entryData.$capHoursEnd,
    $capHoursAvg: entryData.$capHoursAvg,
    $capSequence: entryData.$capSequence,

    // THUG-964
    $creativeAssetID: entryData.$creativeAssetID,
    $creativeTextID: entryData.$creativeTextID,
    $creativeCTA: entryData.$creativeCTA,

    // LY Ads attribution captured from the URL the tracker saw.
    lyAdsClickID: getCapturedLyAdsClickID(),

    // Marketing community posts info
    $pageLinkId: entryData.$pageLinkId,
    $pageLinkType: entryData.$pageLinkType,

    // THUG-1052
    sourceUpdatePushConsecutive: entryData.pushConsecutive,

    // THUG-1051
    sourceUpdatePushExpected: entryData.pushExpected,

    isAppSwitch: entryData.isAppSwitch,

    // Match2 Game Save State
    saveStateAvailable: hasValidSaveState(user, now),

    // Image formats support
    supportsWebP: args.supportsWebP,
    supportsAvif: args.supportsAvif,

    // True if user is returning to LINE from guest mode
    isReturningToLINE:
      hasWebPlayerCredentials(config.appName) &&
      GCInstant.getPreviousEntryTimestamp(),

    // Rich menu assigned for user
    lineAssignedRichMenuID: getDefaultRichMenu(user),
  };

  const openWebBehavior = getOpenWebBehavior(user);

  // First session user properties
  const firstEntryUserProps: FirstEntryUserProperties = {
    // TODO Deprecated, remove someday
    $firstEntrySourcePlatformOS: entryData.$platformOS,
    $firstEntrySourcePlatformApp: entryData.$platformApp,
    $firstEntrySourcePlatformAppOS: entryData.$platformAppOS,

    // Neo
    $firstEntrySourceRealtimeIsPurchaser: entryData.$realtimeIsPurchaser,
    $firstEntrySourceRealtimeLTV: entryData.$realtimeLTV,
    $firstEntrySourceRealtimeLocale: entryData.$realtimeLocale,

    // iOS native app
    $firstEntryAppleVersion:
      GCInstant.osType === 'IOS_APP'
        ? GCInstant.legacyNative?.platform.version
        : undefined,

    // #6151 firstEntryOpenWebBehaviour
    firstEntryOpenWebBehavior: openWebBehavior,
  };

  const timeSinceLastNativeLaunch =
    now -
    Math.max(user.native.appleLastLaunched, user.native.googleLastLaunched);

  // Last session user properties
  const lastEntryUserProps: LastEntryUserProperties = {
    // TODO Deprecated, remove someday
    $platformOS: $platform.os,
    $platformApp: $platform.app,
    $platformAppOS: `${$platform.app}_${$platform.os}`,

    // TODO Deprecated, remove someday
    $lastEntrySourcePlatformOS: entryData.$platformOS,
    $lastEntrySourcePlatformApp: entryData.$platformApp,
    $lastEntrySourcePlatformAppOS: entryData.$platformAppOS,

    // Neo
    $lastEntryIsPurchaser: realtimeProps.$realtimeIsPurchaser,
    $lastEntryLTV: realtimeProps.$realtimeLTV,

    $lastEntrySourceRealtimeIsPurchaser: entryData.$realtimeIsPurchaser,
    $lastEntrySourceRealtimeLTV: entryData.$realtimeLTV,
    $lastEntrySourceRealtimeLocale: entryData.$realtimeLocale,

    // Native app
    $lastEntryAppleVersion:
      GCInstant.osType === 'IOS_APP'
        ? (GCInstant.legacyNative?.platform.version as string)
        : undefined,

    // THUG-1190
    $lastEntryPurchaseDaysElapsed: getPurchaseDaysElapsed(now, purchaseHistory),

    // Entry Scene
    $lastEntryScene: getLaunchScene(),

    // Native app.
    isInNative: isPlatformNative(),
    timeSinceLastNativeLaunch,

    // Native app push notification properties
    ...PNsManager.getPushNotificationUserProperties(),

    // If user logged in once as guest we save flag into local storage and check it here
    // @see src/lib/onLogin.ts
    // https://github.com/play-co/buruburu/issues/5935
    ...(localStorage.getItem('hasPlayedAsGuest') === 'true'
      ? { hasPlayedAsGuest: true }
      : {}),

    // https://github.com/play-co/buruburu/issues/6015
    // If user re-install app it will clear local storage, and we can detect possible re-install by checking this flag and local storage flag.
    lastEntryIsFirstOfNativeInstall:
      // First ever install case
      (isPlatformNative() &&
        GCInstant.storage.entry.lastNativeAndroidEntry === 0 &&
        GCInstant.storage.entry.lastNativeIOSEntry === 0) ||
      // Native app re-install case
      (isPlatformNative() &&
        user.native.isFirstOfNativeInstallTracked &&
        !localStorage.getItem('lastEntryIsFirstOfNativeInstall')),

    // Match2 level
    lastEntryMatchLevel: user.match2.level,

    // Daily bonus collect count
    lastEntryDailyBonusCollectCount: user.match2.dailyBonus.totalCount,

    // #6073 lastEntryOpenWebBehaviour
    lastEntryOpenWebBehavior: openWebBehavior,
  };

  // Send off all the props to GCInstant
  return GCInstant.sendEntryFinalAnalytics(
    entryFinalEventProps,
    firstEntryUserProps,
    lastEntryUserProps,
  );
}

export function makePayload($channel: ChannelProperty): PayloadProperties {
  const lastProps = analytics.getUserProperties();

  return {
    $channel: $channel,

    // Last session user properties
    // TODO Deprecated, remove someday
    $platformOS: lastProps.$platformOS,
    $platformApp: lastProps.$platformApp,
    $platformAppOS: lastProps.$platformAppOS,

    // Neo
    $realtimeIsPurchaser: lastProps.$realtimeIsPurchaser,
    $realtimeLTV: lastProps.$realtimeLTV,
    $realtimeLocale: lastProps.$realtimeLocale,
  };
}
